import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2 } from 'lucide-react';
import { MfaEnrollmentGate } from '@/components/auth/MfaEnrollmentGate';

export default function ProtectedRoute({ children, requiredRoles = [], redirectTo = null }) {
  const { isAuthenticated, loading, user, hasRole} = useAuth();
  const location = useLocation();

  // Show loading spinner while checking auth
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Determine redirect path based on the current route
  const getRedirectPath = () => {
    if (redirectTo) return redirectTo;
    
    // Custom redirects for specific routes
    if (location.pathname.startsWith('/corvana-crm')) {
      return '/crm-login';
    }
    if (location.pathname.startsWith('/tech-admin')) {
      return '/tech-admin/login';
    }
    
    return '/login';
  };

  // Redirect to appropriate login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to={getRedirectPath()} state={{ from: location }} replace />;
  }

  // Check for required roles if specified
  if (requiredRoles.length > 0) {
    const hasRequiredRole = requiredRoles.some(role => hasRole(role));
    
    // Also allow @corvana.com.au and @corvana.io emails for CRM access
    const isCorvanaStaff = user?.email?.endsWith('@corvana.com.au') || user?.email?.endsWith('@corvana.io');
    
    if (!hasRequiredRole && !isCorvanaStaff) {
      // Demo / sales / other client-level roles have their home at /app — send
      // them straight there instead of a dead-end staff-only "Access Denied"
      // screen. /app has no role requirement, so there's no redirect loop.
      const roles = user?.roles || (user?.role ? [user.role] : []);
      const isClientLevel =
        user?.is_demo === true ||
        ['sales', 'support', 'staff', 'management', 'tenant_user', 'user', 'demo'].some(
          (r) => roles.includes(r)
        );
      if (isClientLevel && location.pathname !== '/app') {
        return <Navigate to="/app" replace />;
      }
      return (
        <div className="min-h-screen bg-background flex items-center justify-center" data-testid="access-denied-screen">
          <div className="text-center p-8 glass-card rounded-xl max-w-md">
            <h2 className="text-xl font-semibold text-destructive mb-2">Access Denied</h2>
            <p className="text-muted-foreground mb-4">
              You don't have permission to access this page.
            </p>
            <a href="/app" className="text-primary hover:underline" data-testid="access-denied-return-link">
              Return to Dashboard
            </a>
          </div>
        </div>
      );
    }
  }

  // Enforce MFA enrolment on every protected tree EXCEPT /app — MainLayout
  // already wraps /app content with the gate (so its grace nudge sits inside
  // the app chrome). Without this, areas like /corvana-crm hit the server-side
  // MFA block with no UI prompt — a silent "failed to load" dead end.
  const underAppLayout = location.pathname === '/app' || location.pathname.startsWith('/app/');
  if (underAppLayout) return children;
  return <MfaEnrollmentGate>{children}</MfaEnrollmentGate>;
}
